import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import React from 'react';
import { setAnswerPageConfig } from '/@contexts/answer-context';
import {
    refetchVizData,
    setNextGenNoShouldReplaceCurrent,
    setVisualizationLoading,
} from '/@services/answer/answer.util';
import {
    setPinboardLoading,
    setPinboardMutationInProgress,
} from '/@services/pinboard/pinboard.util';
import { BlinkErrorCode } from '/@utils/error-handling/blink-error-boundary';
import {
    ApolloError,
    DocumentNode,
    FetchResult,
    InternalRefetchQueriesInclude,
    MutationHookOptions,
    Unmasked,
    useApolloClient,
    useMutation,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { useCancelableMutation } from '../cancelable/cancelable-hooks';
import { LoadingState } from '../generated/graphql-types';
import { setSageBarLoading } from '../search/search-service.util';
import {
    BachMutationFunctionOptions,
    BachMutationHookOptions,
    BachMutationTuple,
    publishBachMutationEvent,
} from './bach.util';

const logger = create('Bach-Base');

export function useBachMutationWithoutSession<TData, TVariables>(
    sessionId: string,
    mutation: DocumentNode,
    options: BachMutationHookOptions<TData, TVariables> = {},
    handledErrors?: BlinkErrorCode[],
): BachMutationTuple<
    TData,
    Omit<TVariables, 'session' | 'dataPaginationParams'>
> {
    // Temporary logs to help debug the sage bar infinite loading issue observed in E2Es.
    const mutationName = (mutation.definitions[0] as any)?.name?.value;
    const gqlOperationName = (mutation.definitions[0] as any)?.selectionSet
        ?.selections[0]?.name?.value;

    const client = useApolloClient();

    const params: MutationHookOptions<TData, TVariables> = {
        ...options,
        onError: (error: ApolloError) => {
            if (options.setNextGenNoShouldReplaceCurrent) {
                setNextGenNoShouldReplaceCurrent(client, sessionId, false);
            }
            if (options.setVisualizationsLoading) {
                setVisualizationLoading(
                    client,
                    sessionId,
                    LoadingState.Failure,
                );
            }
            if (options.setPhrasesLoading) {
                setSageBarLoading(client, sessionId, LoadingState.Failure);
            }
            if (options.setPinboardLoading) {
                setPinboardLoading(client, sessionId, LoadingState.Failure);
            }
            if (options.trackPinboardMutationProgress) {
                setPinboardMutationInProgress(client, sessionId, false);
            }
            publishBachMutationEvent<TData, TVariables>(
                mutation,
                LoadingState.Failure,
                sessionId,
                error,
            );
            return options.onError?.(error);
        },
        onCompleted: (data: TData) => {
            if (options.setNextGenNoShouldReplaceCurrent) {
                setNextGenNoShouldReplaceCurrent(client, sessionId, false);
            }
            if (options.setVisualizationsLoading) {
                setVisualizationLoading(
                    client,
                    sessionId,
                    LoadingState.Success,
                );
            }
            if (options.setPhrasesLoading) {
                setSageBarLoading(client, sessionId, LoadingState.Success);
            }
            if (options.setPinboardLoading) {
                setPinboardLoading(client, sessionId, LoadingState.Success);
            }
            if (options.trackPinboardMutationProgress) {
                setPinboardMutationInProgress(client, sessionId, false);
            }
            publishBachMutationEvent<TData, TVariables>(
                mutation,
                LoadingState.Success,
                sessionId,
                data,
            );
            return options.onCompleted?.(data);
        },
        handledErrors,
    };

    if (options.shouldRefetchData) {
        // updateTableDataStartOffset is used to reset table data offset while fetching new data. This should be moved from here to be added in
        // refetchVizData, currently having cyclic depnedency on bach.ts and answer.util so keeping here.
        // need to set the updateTableDataStartOffset on cache
        let setOffsetCB: (startOffset: number) => void;
        try {
            const { updateTableDataStartOffset } = setAnswerPageConfig();
            setOffsetCB = updateTableDataStartOffset;
        } catch (e) {
            logger.warn(e);
        }

        // We are about to overwrite refetchQueries, so check if the user tried to pass something
        // there and warn them if so
        if (options.refetchQueries) {
            logger.error(
                'When shouldRefetchData is true, any value in refetchQueries will be overwritten. Please use customRefetchQueriesFn instead.',
            );
        }

        // Type assertion to handle Apollo Client 3.13.5 compatibility
        (params as MutationHookOptions<TData, TVariables> & {
            refetchQueries?: unknown;
        }).refetchQueries = (
            result: FetchResult<Unmasked<TData>>,
        ): InternalRefetchQueriesInclude => {
            if (_.isFunction(setOffsetCB)) {
                setOffsetCB(0);
            }
            if (options.customRefetchQueriesFn) {
                return options.customRefetchQueriesFn(
                    client,
                    result as FetchResult<TData>,
                );
            }
            // Handle Apollo Client 3.13.5 Unmasked<TData> to TData conversion
            return refetchVizData(client, result as FetchResult<TData>);
        };
        params.awaitRefetchQueries = _.isBoolean(options.awaitForQueries)
            ? options.awaitForQueries
            : true;
    }

    const [mutate, , state] = useCancelableMutation(
        useMutation<TData, TVariables>(mutation, params),
        true,
        params?.context?.fetchOptions?.signal,
    );

    const mutateWithLoader = React.useCallback(
        (opts: BachMutationFunctionOptions<TData, TVariables>) => {
            if (
                opts?.setNextGenNoShouldReplaceCurrent ??
                options.setNextGenNoShouldReplaceCurrent
            ) {
                setNextGenNoShouldReplaceCurrent(client, sessionId, true);
            }
            if (
                opts?.setVisualizationsLoading ??
                options.setVisualizationsLoading
            ) {
                setVisualizationLoading(
                    client,
                    sessionId,
                    LoadingState.InProgress,
                );
            }
            if (opts?.setPhrasesLoading ?? options.setPhrasesLoading) {
                setSageBarLoading(client, sessionId, LoadingState.InProgress);
            }
            if (opts?.setPinboardLoading ?? options.setPinboardLoading) {
                setPinboardLoading(client, sessionId, LoadingState.InProgress);
            }
            if (
                opts?.trackPinboardMutationProgress ??
                options.trackPinboardMutationProgress
            ) {
                setPinboardMutationInProgress(client, sessionId, true);
            }

            const variables = {
                ...options?.variables,
                ...opts?.variables,
            };

            publishBachMutationEvent<TData, TVariables>(
                mutation,
                LoadingState.InProgress,
                sessionId,
                variables,
            );

            return mutate({
                ...opts,
                variables,
            });
        },
        [
            sessionId,
            mutate,
            options.setVisualizationsLoading,
            options.setPhrasesLoading,
            options.setPinboardLoading,
            options.trackPinboardMutationProgress,
            ...Object.values(options.variables ?? {}),
        ],
    );

    return [mutateWithLoader, state];
}
